import { useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  BIFROST_AGENT_AUTH,
  BIFROST_ARCHITECTURE,
  BIFROST_CAPABILITIES,
  BIFROST_FLOW,
  BIFROST_HERO,
  BIFROST_INDUSTRIES,
  BIFROST_PAGES,
  BIFROST_PATHS,
  BIFROST_PROBLEM,
  BIFROST_SECURITY_REVIEWS,
  BIFROST_VERIFY,
  BIFROST_WHY,
  type BifrostPageDef,
} from "../../content/bifrost-site";
import type { CtaLink } from "../../content/types";
import { ActionDemo, FederatedFlow, ReceiptVerifyDemo } from "./BifrostDemos";
import "./bifrost.css";
import { TrustExchange } from "./TrustExchange";

function Cta({ cta, primary = false }: { cta: CtaLink; primary?: boolean }) {
  const className = primary ? "btn btn--primary" : "btn btn--quiet";
  return cta.external ? (
    <a className={className} href={cta.href}>{cta.label}</a>
  ) : (
    <Link className={className} to={cta.href}>{cta.label}</Link>
  );
}

function SubNav() {
  return (
    <nav className="bf-subnav" aria-label="Bifrost">
      <Link to="/bifrost" className="bf-subnav__brand">
        <img src="/brand/bifrost-mark.png" alt="" width={24} height={24} />
        <b>Bifrost</b>
      </Link>
      <ul>
        {BIFROST_PAGES.map((p) => (
          <li key={p.key}>
            <NavLink to={p.path} end>{p.navLabel}</NavLink>
          </li>
        ))}
      </ul>
      <Link className="btn btn--primary bf-subnav__cta" to="/contact?topic=bifrost-demo">Request a Demo</Link>
    </nav>
  );
}

function PageHead({ eyebrow, heading, body }: { eyebrow: string; heading: string; body: string[] | string }) {
  const paragraphs = Array.isArray(body) ? body : [body];
  return (
    <header className="bf-head">
      <p className="bf-eyebrow">{eyebrow}</p>
      <h1>{heading}</h1>
      {paragraphs.map((p) => <p key={p} className="bf-lede">{p}</p>)}
    </header>
  );
}

function NextPage({ current }: { current: BifrostPageDef }) {
  const index = BIFROST_PAGES.findIndex((p) => p.key === current.key);
  const next = BIFROST_PAGES[index + 1];
  if (!next) return null;
  return (
    <Link className="bf-next" to={next.path}>
      <span>Next</span>
      <b>{next.navLabel} →</b>
    </Link>
  );
}

function Paths() {
  return (
    <section className="bf-paths" aria-labelledby="bf-paths">
      <h2 id="bf-paths">{BIFROST_PATHS.heading}</h2>
      <div className="bf-paths__grid">
        {BIFROST_PATHS.items.map((p, i) => (
          <article key={p.title}>
            <h3>{p.title}</h3>
            <p>{p.audience}</p>
            <Cta cta={p.cta} primary={i === 0} />
          </article>
        ))}
      </div>
    </section>
  );
}

function Overview() {
  return (
    <>
      <section className="bf-hero">
        <p className="bf-eyebrow">{BIFROST_HERO.eyebrow}</p>
        <h1>{BIFROST_HERO.h1}</h1>
        <p className="bf-hero__supporting">{BIFROST_HERO.supporting}</p>
        {BIFROST_HERO.body.map((p) => <p key={p} className="bf-lede">{p}</p>)}
        <div className="bf-ctas">
          {BIFROST_HERO.ctas.map((c, i) => <Cta key={c.href} cta={c} primary={i === 0} />)}
        </div>
        <ul className="bf-pillars">
          {BIFROST_HERO.pillars.map((p) => <li key={p}>{p}</li>)}
        </ul>
      </section>

      <section className="bf-section">
        <h2>{BIFROST_PROBLEM.heading}</h2>
        <div className="bf-split">
          <div>{BIFROST_PROBLEM.body.map((p) => <p key={p} className="bf-body">{p}</p>)}</div>
          <div className="bf-questions">
            {BIFROST_PROBLEM.questions.map((q) => (
              <article key={q.q}><b>{q.q}</b><span>{q.a}</span></article>
            ))}
          </div>
        </div>
        <p className="bf-statement">{BIFROST_PROBLEM.closing}</p>
      </section>

      <TrustExchange />

      <section className="bf-section">
        <h2>{BIFROST_WHY.heading}</h2>
        <div className="bf-grid bf-grid--4">
          {BIFROST_WHY.items.map((w) => (
            <article key={w.title} className="bf-card"><h3>{w.title}</h3><p>{w.body}</p></article>
          ))}
        </div>
        <p className="bf-statement">{BIFROST_WHY.closing}</p>
      </section>

      <section className="bf-section">
        <h2>The full story</h2>
        <div className="bf-grid bf-grid--3 bf-story">
          {BIFROST_PAGES.slice(1).map((p) => (
            <Link key={p.key} to={p.path} className="bf-card bf-card--link">
              <span className="bf-kicker">{p.navLabel}</span>
              <p>{p.lede}</p>
              <b>Read →</b>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

function Platform() {
  return (
    <>
      <PageHead eyebrow="Platform" heading={BIFROST_CAPABILITIES.heading} body={BIFROST_CAPABILITIES.body} />
      <section className="bf-section">
        <div className="bf-grid bf-grid--3">
          {BIFROST_CAPABILITIES.items.map((c) => (
            <article key={c.title} className="bf-card bf-card--cap">
              <h3>{c.title}</h3>
              <ul>{c.points.map((p) => <li key={p}>{p}</li>)}</ul>
            </article>
          ))}
        </div>
      </section>
      <section className="bf-section bf-section--dark">
        <h2>{BIFROST_FLOW.heading}</h2>
        <p className="bf-body">{BIFROST_FLOW.body}</p>
        <FederatedFlow />
      </section>
    </>
  );
}

function AgentAuthorization() {
  return (
    <>
      <PageHead eyebrow="Cross-organization agent authorization" heading={BIFROST_AGENT_AUTH.heading} body={BIFROST_AGENT_AUTH.body} />
      <p className="bf-statement">{BIFROST_AGENT_AUTH.statement}</p>
      <section className="bf-section bf-section--dark">
        <h2>Walk through a federated action</h2>
        <ActionDemo />
      </section>
      <p className="bf-boundary">{BIFROST_AGENT_AUTH.boundary}</p>
    </>
  );
}

function Industries() {
  return (
    <>
      <PageHead eyebrow={BIFROST_INDUSTRIES.supporting} heading={BIFROST_INDUSTRIES.heading} body={BIFROST_INDUSTRIES.body} />
      <section className="bf-section">
        <div className="bf-grid bf-grid--4">
          {BIFROST_INDUSTRIES.items.map((i) => (
            <article key={i.name} className="bf-card"><h3>{i.name}</h3><p>{i.body}</p></article>
          ))}
        </div>
        <p className="bf-note">{BIFROST_INDUSTRIES.note}</p>
      </section>
    </>
  );
}

function SecurityReviews() {
  return (
    <>
      <PageHead eyebrow="Security reviews · the immediate business value" heading={BIFROST_SECURITY_REVIEWS.heading} body={BIFROST_SECURITY_REVIEWS.body} />
      <section className="bf-section">
        <ol className="bf-timeline">
          {BIFROST_SECURITY_REVIEWS.steps.map((s, i) => (
            <li key={s.title}><span>{i + 1}</span><div><b>{s.title}</b><p>{s.body}</p></div></li>
          ))}
        </ol>
      </section>
      <section className="bf-section">
        <div className="bf-grid bf-grid--2">
          {BIFROST_SECURITY_REVIEWS.sides.map((s) => (
            <article key={s.title} className="bf-card"><h3>{s.title}</h3><p>{s.body}</p></article>
          ))}
        </div>
        <div className="bf-ctas"><Cta cta={BIFROST_SECURITY_REVIEWS.cta} primary /></div>
      </section>
    </>
  );
}

function Verify() {
  return (
    <>
      <PageHead eyebrow="Independent verification" heading={BIFROST_VERIFY.heading} body={BIFROST_VERIFY.body} />
      <section className="bf-section bf-section--dark">
        <h2>Verify a receipt</h2>
        <ReceiptVerifyDemo />
        <p className="bf-note">Synthetic sample bundles shipped with @odingard/bifrost-verify {BIFROST_VERIFY.version}; the report shown is the CLI’s actual output.</p>
        <pre className="bf-install">{BIFROST_VERIFY.install}</pre>
        <div className="bf-ctas"><Cta cta={BIFROST_VERIFY.cta} primary /><Cta cta={{ label: "Verify an action chain", href: "/bifrost/agent-authorization" }} /></div>
      </section>
    </>
  );
}

function Architecture() {
  return (
    <>
      <PageHead eyebrow="Developer and enterprise architecture" heading={BIFROST_ARCHITECTURE.heading} body={BIFROST_ARCHITECTURE.body} />
      <section className="bf-section bf-section--dark">
        <FederatedFlow compact />
      </section>
      <section className="bf-section">
        <div className="bf-grid bf-grid--3">
          {BIFROST_ARCHITECTURE.items.map((i) => (
            <article key={i.name} className="bf-card"><h3>{i.name}</h3><p>{i.body}</p></article>
          ))}
        </div>
        <p className="bf-boundary">{BIFROST_ARCHITECTURE.boundary}</p>
        <p className="bf-note">{BIFROST_ARCHITECTURE.deployment}</p>
      </section>
      <section className="bf-section">
        <h2>Technical documentation</h2>
        <div className="bf-grid bf-grid--4">
          {BIFROST_ARCHITECTURE.docs.map((d) => (
            <a key={d.href} className="bf-card bf-card--link" href={d.href}>
              <span className="bf-kicker">{d.kind}</span><b>{d.label}</b>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}

const BODIES = {
  overview: Overview,
  platform: Platform,
  "agent-authorization": AgentAuthorization,
  industries: Industries,
  "security-reviews": SecurityReviews,
  verify: Verify,
  architecture: Architecture,
} as const;

export function BifrostPage({ page }: { page: BifrostPageDef }) {
  useEffect(() => {
    document.title = `${page.title} — Odingard`;
  }, [page]);
  const Body = BODIES[page.key];
  return (
    <main className="page bf" id="main">
      <SubNav />
      <Body />
      <NextPage current={page} />
      <Paths />
    </main>
  );
}
